/**
 * Dimension unit conversion utilities for Mộc Phát ERP.
 *
 * Core rule:
 * Underlying data in database is always stored in millimeters (mm).
 * Views can toggle display between inch (rounded to 1 decimal place by default) and mm.
 * Numbers are displayed cleanly without unit suffix inside cells.
 */

export const MM_PER_INCH = 25.4

/**
 * Converts millimeters to inches.
 * @param mm Value in millimeters
 * @param decimals Decimal places to round to (default 1)
 */
export function mmToInch(mm: number, decimals: number = 1): number {
  if (mm === 0) return 0
  const inches = mm / MM_PER_INCH
  const factor = Math.pow(10, decimals)
  return Math.round(inches * factor) / factor
}

/**
 * Converts inches to millimeters (rounded to nearest integer).
 * @param inch Value in inches
 */
export function inchToMm(inch: number): number {
  if (inch === 0) return 0
  return Math.round(inch * MM_PER_INCH)
}

/**
 * Formats a millimeter dimension value for display in either 'mm' or 'inch'.
 * Returns string number without unit suffix.
 * Returns '—' for null or undefined.
 */
export function formatDim(
  val: number | null | undefined,
  unit: 'mm' | 'inch' = 'inch',
  decimals: number = 1,
): string {
  if (val == null || isNaN(Number(val))) {
    return '—'
  }
  const num = Number(val)
  if (unit === 'inch') {
    return String(mmToInch(num, decimals))
  }
  return String(Math.round(num))
}
